import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, type HeroSlide, type Post, type GalleryImage, type Stat, type ContactMessage, type NewsletterSubscriber, type Profile } from "@/lib/supabase";

export default function DashboardOverview() {
  const navigate = useNavigate();
  const [statsData, setStatsData] = useState({
    users: 0, posts: 0, gallery: 0, hero: 0, messages: 0, subscribers: 0,
  });
  const [latestPosts, setLatestPosts] = useState<Post[]>([]);
  const [recentMessages, setRecentMessages] = useState<ContactMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const [
        { count: usersCount },
        { count: postsCount },
        { count: galleryCount },
        { count: heroCount },
        { count: messagesCount },
        { count: subscribersCount },
        { data: postsData },
        { data: messagesData },
      ] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("posts").select("*", { count: "exact", head: true }),
        supabase.from("gallery").select("*", { count: "exact", head: true }),
        supabase.from("hero_slides").select("*", { count: "exact", head: true }),
        supabase.from("contact_messages").select("*", { count: "exact", head: true }),
        supabase.from("newsletter_subscribers").select("*", { count: "exact", head: true }),
        supabase.from("posts").select("*").order("created_at", { ascending: false }).limit(4),
        supabase.from("contact_messages").select("*").order("created_at", { ascending: false }).limit(3),
      ]);
      setStatsData({
        users: usersCount || 0, posts: postsCount || 0, gallery: galleryCount || 0,
        hero: heroCount || 0, messages: messagesCount || 0, subscribers: subscribersCount || 0,
      });
      setLatestPosts(postsData || []);
      setRecentMessages(messagesData || []);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const stats = [
    { label: "Total Users", value: statsData.users, icon: "ri-user-line", color: "bg-primary-50 text-primary-600", route: "users" },
    { label: "Total Posts", value: statsData.posts, icon: "ri-article-line", color: "bg-gold-50 text-gold-600", route: "posts" },
    { label: "Gallery Images", value: statsData.gallery, icon: "ri-image-line", color: "bg-emerald-50 text-emerald-600", route: "gallery" },
    { label: "Hero Slides", value: statsData.hero, icon: "ri-slideshow-line", color: "bg-purple-50 text-purple-600", route: "hero" },
    { label: "Contact Messages", value: statsData.messages, icon: "ri-mail-line", color: "bg-red-50 text-red-500", route: "messages" },
    { label: "Newsletter Subscribers", value: statsData.subscribers, icon: "ri-mail-send-line", color: "bg-sky-50 text-sky-600", route: "newsletter" },
  ];

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="h-28 bg-gray-100 rounded-xl" />))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <h1 className="font-heading font-bold text-2xl md:text-3xl text-primary-900 mb-6">Dashboard Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-8">
        {stats.map((stat) => (
          <button key={stat.label} onClick={() => navigate(`/admin/${stat.route}`)} className="bg-white rounded-xl p-4 md:p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all text-left">
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}><i className={`${stat.icon} text-lg md:text-xl`} /></div>
            <p className="font-heading font-bold text-xl md:text-2xl text-gray-900">{stat.value}</p>
            <p className="text-gray-500 text-xs md:text-sm mt-1">{stat.label}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-gray-100">
            <h2 className="font-heading font-bold text-base md:text-lg text-primary-900">Latest Posts</h2>
            <button onClick={() => navigate("/admin/posts")} className="text-sm text-gold-600 hover:text-gold-700 font-medium">View All</button>
          </div>
          <div className="divide-y divide-gray-100">
            {latestPosts.map((post) => (
              <div key={post.id} className="px-5 md:px-6 py-3.5">
                <p className="font-medium text-sm text-gray-800 mb-1">{post.title}</p>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className={`px-2 py-0.5 rounded-full text-white ${post.category === "event" ? "bg-gold-500" : post.category === "news" ? "bg-primary-600" : "bg-emerald-600"}`}>{post.category}</span>
                  <span>{new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  <span className={post.status === "published" ? "text-emerald-600" : "text-yellow-600"}>{post.status}</span>
                </div>
              </div>
            ))}
            {latestPosts.length === 0 && <div className="px-6 py-8 text-center text-gray-400 text-sm">No posts yet</div>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-gray-100">
            <h2 className="font-heading font-bold text-base md:text-lg text-primary-900">Recent Messages</h2>
            <button onClick={() => navigate("/admin/messages")} className="text-sm text-gold-600 hover:text-gold-700 font-medium">View All</button>
          </div>
          <div className="divide-y divide-gray-100">
            {recentMessages.map((msg) => (
              <div key={msg.id} className="px-5 md:px-6 py-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-gray-800 truncate">{msg.name}</p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{msg.subject}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{new Date(msg.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                </div>
              </div>
            ))}
            {recentMessages.length === 0 && <div className="px-6 py-8 text-center text-gray-400 text-sm">No messages yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
}